/*	Copyright (c) 1996 - 2026 NoWatt Ltd				*/
/*	  All Rights Reserved  											*/

/*	THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF NoWatt Ltd	*/
/*	The copyright notice above does not evidence any   				*/
/*	actual or intended publication of such source code.				*/

'use strict';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Wunderbaum } from 'wunderbaum';
import moment from 'moment';
import DateComponent from './DateComponent';
import Layout from './Layout';
import ChannelType from '../../../lib/channelTypes.js';

type ChannelNode = {
  key: string | number;
  type?: string | number | null;
  title: string;
  lazy?: boolean;
  children?: ChannelNode[] | null;
  data: {
    folder?: boolean;
    parentid?: string | number;
  };
  getColElem(index: number): HTMLElement | null;
  getLevel(): number;
  isExpanded(): boolean;
  setExpanded(expanded: boolean): void;
  toggleSelected(): void;
  setSelected(selected: boolean): void;
};

const getChannelType = (type: ChannelNode['type']): number | null => {
  if (type === null || type === undefined || type === '') return null;
  const parsed = Number(type);

  return Number.isNaN(parsed) ? null : parsed;
};

type UsageDataResponse = {
  values: Array<[number, number]>;
  unit?: string;
};

type DataCallbackPayload = {
  from: number;
  to: number;
  unit: string;
  values: number[][];
};

type ChartSeries = {
  id: string;
  stack: string;
  name: string;
  type: 'line' | 'columns';
  valueAxis: string;
  enabled: boolean;
  data: { index: number };
  style: { smoothing: boolean; lineWidth: number } | { depth: number };
};

interface ChannelTreeTableProps {
  url: string;
  fromx: moment.Moment;
  tox: moment.Moment;
  updatenodes: (nodes: ChannelNode[]) => void;
  nextslide: (tag: string) => void;
}

interface LineChartProps {
  fromx: moment.Moment;
  tox: moment.Moment;
  nodes: ChannelNode[];
  update: (from: moment.Moment, to: moment.Moment) => void;
  prevslide: (tag: string) => void;
}

interface SwipePaneProps {
  fromx: moment.Moment;
  tox: moment.Moment;
  nodes: ChannelNode[];
  update: (from: moment.Moment, to: moment.Moment) => void;
  updatenodes: (nodes: ChannelNode[]) => void;
}

interface UsagePageProps {
  user: string | null;
}

const previousPeriod = (obj: { fromx: moment.Moment; tox: moment.Moment }) => {
  if(obj.fromx.isBefore(moment(obj.tox).subtract(7, 'days'))) {
    if(obj.fromx.isBefore(moment(obj.tox).subtract(1, 'months'))) {
      // More than a month, use previous year
      return { fromx: obj.fromx.subtract(1, 'years'), tox: obj.tox.subtract(1, 'years') }
    }
    else {
      // Less than a month, use period month before
      return { fromx: obj.fromx.subtract(1, 'months'), tox: obj.tox.subtract(1, 'months') }
    }
  }
  else {
    // Less than a full week, use period week before
    return { fromx: obj.fromx.subtract(7, 'days'), tox: obj.tox.subtract(7, 'days') }
  }
};

const ChannelTreeTable = ({ url, fromx, tox, updatenodes, nextslide }: ChannelTreeTableProps) => {
  const treeRef = useRef<Wunderbaum | null>(null);
  const updateTreeRef = useRef<((node: ChannelNode[], fromx: moment.Moment, tox: moment.Moment) => void) | null>(null);
  const usageCacheRef = useRef<Record<string, { align: string; html: string }>>({});
  const inFlightRef = useRef(new Map<string, Element>());
  const fromCheckboxRef = useRef(false);

  // Keep latest props accessible inside Wunderbaum callbacks without stale closures
  const propsRef = useRef({ url, fromx, tox, updatenodes, nextslide });
  useEffect(() => {
    propsRef.current = { url, fromx, tox, updatenodes, nextslide };
  });

  const getType = (type: ChannelNode['type']) => {
    switch(getChannelType(type)) {
      case ChannelType.WATER: case ChannelType.WATER_SPUR: return 'ltr';
      case ChannelType.TEMPERATURE: return '˚C';
      case ChannelType.BINARY: return '%';
      default: return '';
    }
  };

  const updateUsed = useCallback((url: string, type: ChannelNode['type'], domEl: Element) => {
    const cached = usageCacheRef.current[url];
    if(cached) {
      domEl.setAttribute("align", cached.align);
      domEl.innerHTML = cached.html;
      return;
    }
    domEl.setAttribute("align", "left");
    domEl.textContent = ' Calculating...';
    if(inFlightRef.current.has(url)) {
      inFlightRef.current.set(url, domEl);
      return;
    }
    inFlightRef.current.set(url, domEl);
    fetch(url, {cache: 'no-store'})
      .then(response => response.json())
      .then((data: UsageDataResponse) => {
        const el = inFlightRef.current.get(url);
        inFlightRef.current.delete(url);
        if (!el) return;
        let align = 'right', html;
        if(data.values.length > 0) {
          if(data.values[0][1] > -1000) {
            html = data.values[0][1].toFixed(2).toString() + getType(type);
            el.textContent = html;
            html = el.innerHTML;
          } else {
            html = '<span style="color:red">Missing</span>';
            el.innerHTML = html;
          }
        } else {
          html = 'N/A';
          el.textContent = html;
          html = el.innerHTML;
        }
        el.setAttribute("align", align);
        usageCacheRef.current[url] = { align, html };
      })
      .catch(error => {
        inFlightRef.current.delete(url);
        console.error(url, error);
      });
  }, []);

  const updateTree = useCallback((node: ChannelNode[], fromx: moment.Moment, tox: moment.Moment) => {
    const b = fromx.clone().utc();
    const e = tox.clone().utc();
    const prev = previousPeriod({fromx: fromx.clone(), tox: tox.clone()});
    const pb = prev.fromx.utc();
    const pe = prev.tox.utc();

    for(let i = 0; i < node.length; i++) {
      const usedTd = node[i].getColElem(2);
      const previousTd = node[i].getColElem(3);

      if(usedTd) updateUsed(`used/${node[i].key}?from=${b.unix()}&to=${e.unix()}`,
            node[i].type, usedTd);

      if(previousTd) updateUsed(`used/${node[i].key}?from=${pb.unix()}&to=${pe.unix()}`,
            node[i].type, previousTd);

      const childNodes = node[i].children;
      if(childNodes && node[i].isExpanded())
        updateTreeRef.current?.(childNodes, fromx, tox);
    }
  }, [updateUsed]);
  useEffect(() => { updateTreeRef.current = updateTree; }, [updateTree]);

  // Re-fetch when date range changes
  useEffect(() => {
    if(treeRef.current && treeRef.current.root.children) {
      usageCacheRef.current = {};
      inFlightRef.current = new Map();
      updateTree(treeRef.current.root.children, fromx, tox);
    }
  }, [fromx, tox, updateTree]);

  // Mount the Wunderbaum tree once
  useEffect(() => {
    treeRef.current = new Wunderbaum({
      element: document.getElementById('treetable') as HTMLDivElement,
      debugLevel: 0,
      selectMode: 'multi',
      checkbox: true,
      rowHeightPx: 36,
      columns: [
        { id: '*', title: ' ', width: '44px' },
        { id: 'channel', title: 'Channels', width: '*' },
        { id: 'kwh', title: 'KWh', width: '110px', classes: 'wb-helper-end' },
        { id: 'previous', title: 'Previous', width: '110px', classes: 'wb-helper-end' }
      ],
      source: propsRef.current.url,
      receive: (e) => {
        return e.response.channels;
      },
      lazyLoad: (e) => {
        return { url: `${propsRef.current.url}/${e.node.key}` };
      },
      icon: (node: ChannelNode) => {
        const type = getChannelType(node.type);
        if(type === ChannelType.ELECTRICITY || type === ChannelType.ELECTRICITY_SPUR) return "bi bi-activity";
        if(type === ChannelType.CALCULATED) return "bi bi-calculator";
        if(type === ChannelType.GAS || type === ChannelType.GAS_SPUR) return "bi bi-speedometer2";
        if(type === ChannelType.WATER || type === ChannelType.WATER_SPUR) return "bi bi-droplet";
        if(type === ChannelType.TEMPERATURE) return "bi bi-thermometer";
        if(type === ChannelType.BINARY) return "bi bi-toggle-on";
        return '';
      },
      init: async (e) => {
        const saved = localStorage.getItem('wbtree-state');
        if(saved) {
          await e.tree.setState(JSON.parse(saved));
        }
        const nodes = e.tree.getSelectedNodes();
        propsRef.current.updatenodes(nodes);
      },
      click: (e) => {
        const target = e.event?.target as HTMLElement | undefined;
        if (target?.classList.contains('wb-expander') || target?.classList.contains('wb-expander-open')) {
          e.node.setExpanded(!e.node.isExpanded());
          return false;
        }
        if (e.info.colElem?.classList.contains('wb-node') && e.info.region !== 'expander') {
          if ((target as HTMLInputElement | undefined)?.type === 'checkbox') {
            fromCheckboxRef.current = true;
          } else {
            e.node.toggleSelected();
            return false;
          }
        }
      },
      activate: (e) => {
        const fromCheckbox = fromCheckboxRef.current;
        fromCheckboxRef.current = false;
        if (!fromCheckbox) {
          e.node.setSelected(true);
        }
        const nodes = e.tree.getSelectedNodes();
        propsRef.current.updatenodes(nodes);
        if(e.event && !fromCheckbox) propsRef.current.nextslide('#linechart');
      },
      select: (e) => {
        const nodes = e.tree.getSelectedNodes();

        propsRef.current.updatenodes(nodes);
        localStorage.setItem('wbtree-state', JSON.stringify(e.tree.getState({ expandedKeys: true, selectedKeys: true })));
      },
      expand: (e) => {
        localStorage.setItem('wbtree-state', JSON.stringify(e.tree.getState({ expandedKeys: true, selectedKeys: true })));
      },
      render: (e) => {
        if(e.isColspan) return;
        const node = e.node;

        if(e.isNew) {
          const channelCol = node.getColElem(1);
          if(channelCol) {
            channelCol.innerHTML = '';
            const level = node.getLevel();
            for(let i = 1; i < level; i++) {
              const ind = document.createElement('i');
              ind.className = 'wb-indent';
              channelCol.appendChild(ind);
            }
            if(node.lazy || node.data.folder) {
              const expC = document.createElement('i');
              expC.className = 'bi bi-chevron-right wb-expander';
              channelCol.appendChild(expC);
              const expE = document.createElement('i');
              expE.className = 'bi bi-chevron-down wb-expander-open';
              channelCol.appendChild(expE);
            } else {
              const expPlaceholder = document.createElement('i');
              expPlaceholder.className = 'bi bi-chevron-right wb-expander-placeholder';
              channelCol.appendChild(expPlaceholder);
            }
            const type = getChannelType(node.type);
            if(type !== null) {
              const icon = document.createElement('i');

              if(type === ChannelType.ELECTRICITY || type === ChannelType.ELECTRICITY_SPUR) icon.className = 'bi bi-activity';
              else if(type === ChannelType.CALCULATED) icon.className = 'bi bi-calculator';
              else if(type === ChannelType.GAS || type === ChannelType.GAS_SPUR) icon.className = 'bi bi-speedometer2';
              else if(type === ChannelType.WATER || type === ChannelType.WATER_SPUR) icon.className = 'bi bi-droplet';
              else if(type === ChannelType.TEMPERATURE) icon.className = 'bi bi-thermometer';
              else if(type === ChannelType.BINARY) icon.className = 'bi bi-toggle-on';
              channelCol.appendChild(icon);
            }
            const span = document.createElement('span');
            span.textContent = node.title;
            channelCol.appendChild(span);
          }
          const titleSpan = e.nodeElem.querySelector('.wb-title') as HTMLElement | null;
          if(titleSpan) titleSpan.style.display = 'none';
        }
        if(getChannelType(node.type) === null) return;
        const { fromx, tox } = propsRef.current;
        const b = fromx.clone().utc();
        const ev = tox.clone().utc();
        const prev = previousPeriod({fromx: fromx.clone(), tox: tox.clone()});
        const pb = prev.fromx.utc();
        const pe = prev.tox.utc();
        const usedEl = node.getColElem(2);
        const prevEl = node.getColElem(3);
        if(usedEl) updateUsed(`used/${node.key}?from=${b.unix()}&to=${ev.unix()}`,
              node.type, usedEl);
        if(prevEl) updateUsed(`used/${node.key}?from=${pb.unix()}&to=${pe.unix()}`,
              node.type, prevEl);
      }
    });
  }, [updateUsed]);

  return <div id="treetable"></div>;
}

const LineChart = ({ fromx, tox, nodes, update, prevslide }: LineChartProps) => {
  const chartRef = useRef<TimeChartInstance | null>(null);
  // Keep latest callbacks accessible inside chart events without stale closures
  const propsRef = useRef({ fromx, tox, nodes, update, prevslide });
  useEffect(() => {
    propsRef.current = { fromx, tox, nodes, update, prevslide };
  });

  const getData = async (from: number, to: number, unit: string, cb: (data: DataCallbackPayload) => void, fail: (reason: string) => void) => {
    const { nodes } = propsRef.current;
    const b = moment.unix(from).utc();
    const e = moment.unix(to).utc();
    const results: number[][] = [];

    if(from < 0 || to < 0) {
      fail('time range');
      return;
    }

    const promises = nodes.map((node, idx: number) =>
      fetch(`used/${node.key}?from=${b.unix()}&to=${e.unix()}&unit=${unit}`, {cache: 'no-store'})
        .then(response => response.json())
        .then((d: UsageDataResponse) => {
          if(results.length === 0) {
            for(let j = 0; j < d.values.length; j++) {
              results.push([d.values[j][0]]);
            }
          }
          for(let j = 0; j < d.values.length; j++) {
            results[j][idx+1] = d.values[j][1];
          }
        })
        .catch(err => console.error(err))
    );

    await Promise.all(promises);
    const fv = results.length > 0 ? moment.utc(results[0][0]*1000).unix() : from;
    const tv = results.length > 0 ? moment.utc(results[results.length-1][0]*1000).unix() : to;
    cb({ from: fv, to: tv + 1, unit, values: results });
  };

  const genSettings = (nodes: ChannelNode[], testEnabled: boolean, displayUnit: string) => {
    const settings = {
      series: [] as ChartSeries[],
      valueAxis: {
        'litre': { title: 'ltr', side: 'left', enabled: false},
        'percent': { title: '%', side: 'left', enabled: false },
        'kwh': { title: 'KWh', side: 'left', enabled: false },
        'temp': { title: 'Temperature ˚C', side: 'right', enabled: false },
        'watts': { title: 'Watts', side: 'left', enabled: false }
      },
      stacks: {} as Record<string, { name: string; type: string }>
    };
    const colStyle = { depth: 2 };
    const lineStyle = { smoothing: true, lineWidth: 2 };
    const enabledSeries = chartRef.current?.getEnabledSeries() ?? [];

    for(let i = 0; i < nodes.length; i++) {
      const parentId = nodes[i].data.parentid?.toString() ?? '';
      const nodeType = getChannelType(nodes[i].type);
      let stackKey = parentId;
      let enabled = true;
      let valAxis = 'kwh';

      if(testEnabled) {
        enabled = false;
        for(let x = 0; x < enabledSeries.length; x++) {
          if (Number(enabledSeries[x].id) === nodes[i].key) {
            enabled = true;
          }
        }
      }

      if(nodeType === ChannelType.TEMPERATURE) {
        settings.valueAxis['temp'].enabled = true;
        valAxis = 'temp';
      } else if(nodeType === ChannelType.BINARY) {
        settings.valueAxis['percent'].enabled = true;
        valAxis = 'percent';
      } else if(nodeType === ChannelType.WATER || nodeType === ChannelType.WATER_SPUR) {
        settings.valueAxis['litre'].enabled = true;
        valAxis = 'litre';
      } else if(displayUnit === '1 m') {
        settings.valueAxis['watts'].enabled = true;
        valAxis = 'watts';
      } else {
        settings.valueAxis['kwh'].enabled = true;
        valAxis = 'kwh';
      }

      if(displayUnit === '1 m' || nodeType === ChannelType.TEMPERATURE) {
        stackKey = `${i}-${stackKey}`;
      }

      if(displayUnit === '1 m' || nodeType === ChannelType.TEMPERATURE) {
        if(settings.stacks[stackKey] === undefined) {
          settings.stacks[stackKey] = {
            name: parentId,
            type: 'based'
          };
        }
      }

      settings.series.push({
        id: String(nodes[i].key),
        stack: stackKey,
        name: nodes[i].title,
        type: displayUnit === '1 m' || nodeType === ChannelType.TEMPERATURE ? 'line' : 'columns',
        valueAxis: valAxis,
        enabled: enabled,
        data: { index: i+1 },
        style: displayUnit === '1 m' || nodeType === ChannelType.TEMPERATURE ? lineStyle : colStyle
      });
    }

    return settings;
  }

  const nodesChanged = (prevNodes: ChannelNode[], nextNodes: ChannelNode[]) => {
    if(prevNodes.length !== nextNodes.length) return true;
    for(let i = 0; i < prevNodes.length; i++) {
      let j = 0;
      for(; j < nextNodes.length; j++) {
        if(prevNodes[i].key === nextNodes[j].key) break;
      }
      if(j === nextNodes.length) return true;
    }
    return false;
  };

  // Update chart when nodes or date range changes
  const prevNodesRef = useRef(nodes);
  const prevFromxRef = useRef(fromx);
  const prevToxRef = useRef(tox);
  useEffect(() => {
    if(!chartRef.current) return;
    if(nodes.length > 0) {
      const b = fromx.clone().utc();
      const e = tox.clone().utc();

      if(nodesChanged(prevNodesRef.current, nodes)) {
        const s = genSettings(nodes, false, '1 m');
        chartRef.current.replaceSettings(s);
      }

      chartRef.current.reloadData();
      chartRef.current.setDisplayPeriod(`${b.unix()*1000} > ${e.unix()*1000}`, 'now');
    }
    prevNodesRef.current = nodes;
    prevFromxRef.current = fromx;
    prevToxRef.current = tox;
  }, [nodes, fromx, tox]);

  // Mount the chart once
  useEffect(() => {
    const { fromx, tox } = propsRef.current;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const b = fromx.clone().utc();
    const e = tox.clone().utc();
    const settings = {
      assetsUrlBase: 'js/assets',
      area: {
        height: window.innerHeight-50,
        width: window.innerWidth-50,
        displayUnits: [
          { unit: 'm', name: 'minutes' },
          { unit: 'h', name: 'hour' },
          { unit: 'd', name: 'day' },
          { unit: 'w', name: 'week' },
          { unit: 'M', name: 'month' },
          { unit: 'y', name: 'year' }
        ]
      },
      container: document.getElementById("linechart"),
      credits: {
        enabledOnExport: true,
        enabled: true,
        image: 'assets://newlogo.jpg',
        url: 'https://www.nowatt.com'
      },
      toolbar: {
        location: 'outside',
        displayUnit: true,
        displayPeriod: false,
        logScale: false,
        zoomOut: true,
        extraItems: [
        {
            label: 'Tree',
            title: 'Return to tree view',
            align: 'left',
            image: '/js/assets/tree.png',
            onClick: () => {
                const r = chartRef.current?.time();
                if (!r) return;
                if((r[1] - r[0]) > (24*60*60*1000)) {
                  r[0] = r[0] - (moment.unix(r[0]/1000).utcOffset() * 60 * 1000);
                  r[1] = r[1] - (moment.unix(r[1]/1000).utcOffset() * 60 * 1000);
                }
                if(r[0] !== propsRef.current.fromx.valueOf() || r[1] !== propsRef.current.tox.valueOf()) {
                  propsRef.current.update(moment(r[0]), moment(r[1]));
                }
                propsRef.current.prevslide('#treetable');
              }
        }]
      },
      style: {
        columnColors: [ 'rgba(50,205,50,0.5)', 'rgba(52,190,255,0.5)', 'rgba(251,187,48,0.5)', 'rgba(238,52,49,0.5)', 'rgba(137,75,188,0.5)', 'rgba(14,201,172,0.5)', 'rgba(82,75,188,0.5)' ],
        lineColors: [ '#36BEFF', '#FBBD30', '#EE3431', '#894BBC', '#0EC9AC', '#524BBC', '#32CD32', '#FFFFFF' ]
      },
      timeAxis: {
        timeZone: tz,
        minUnitWidth: 0.1,
        unitSizePolicy: 'auto'
      },
      navigation: {
        initialDisplayPeriod: '1 d',
        initialDisplayUnit: 'auto',
        initialDisplayAnchor: 'now'
      },
      interaction: {
        snapMode: 'displayUnit',
        zooming: {
          upDownTreshold: 50,
          sensitivity: 8
        }
      },
      valueAxis: {
        'litre': { title: 'ltr', side: 'left', enabled: false },
        'percent': { title: '%', side: 'left', enabled: false },
        'kwh': { title: 'KWh', side: 'left', enabled: true },
        'temp': { title: 'Temperature ˚C', side: 'right', enabled: false },
        'watts': { title: 'Watts', side: 'left', enabled: false }
      },
      series: [
        { id: '0', name: 'series1', type: 'columns', valueAxis: 'kwh', data:{ index: 1 } }
      ],
      legend: { enabled: true },
      data: {
        timestampInSeconds: true,
        units: ['y', 'M', 'w', 'd', 'h', 'm'],
        dataFunction: (fromx: number, tox: number, unit: string, cb: (data: DataCallbackPayload) => void, fail: (reason: string) => void) => {
          if(propsRef.current.nodes.length > 0) {
            getData(fromx, tox, unit, cb, fail);
          } else {
            cb({ from: fromx, to: tox, values: [], unit });
          }
        },
        prefetchRatio: 0
      },
      events: {
        onChartUpdate: (_e: unknown, t: { displayUnit: string; chart: TimeChartInstance }) => {
          const s = genSettings(propsRef.current.nodes, true, t.displayUnit);
          t.chart.replaceSettings(s);
        }
      },
      currentTime: { enabled: false }
    };

    const Chart = new TimeChart(settings);
    Chart.setDisplayPeriod((b.unix()*1000) + ' > ' + (e.unix()*1000), 'now');
    chartRef.current = Chart;

    const handleResize = () => {
      if (!chartRef.current) return;
      chartRef.current.updateSettings({
        area: {
          height: (window.innerHeight-50),
          width: (window.innerWidth-50)
        }
      });
      chartRef.current.updateSize();
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return <div id='linechart'></div>;
}

const SwipePane = ({ fromx, tox, nodes, update, updatenodes }: SwipePaneProps) => {
  const swiperRef = useRef<SwiperInstance | null>(null);

  const nextslide = (tag: string) => {
    swiperRef.current?.slideNext();
    const target = document.querySelector(tag);
    if (target) {
      window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - 43, behavior: 'smooth' });
    }
  };

  const prevslide = (tag: string) => {
    swiperRef.current?.slidePrev();
    const target = document.querySelector(tag);
    if (target) {
      window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - 43, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    swiperRef.current = new Swiper('.swiper', {
      pagination: { el: '.swiper-pagination', clickable: true },
      keyboard: true,
      simulateTouch: false,
      effect: 'slide'
    });
  }, []);

  return (
    <div className="usage-page">
      <DateComponent fromx={fromx} tox={tox} update={update} />
      <div className="swiper">
        <div className="swiper-wrapper">
          <div className="swiper-slide">
            <ChannelTreeTable url="channels" fromx={fromx} tox={tox}
                updatenodes={updatenodes} nextslide={nextslide}/>
          </div>
          <div className="swiper-slide">
            <LineChart fromx={fromx} tox={tox}
                nodes={nodes} update={update} prevslide={prevslide}/>
          </div>
        </div>
        <div className="swiper-pagination"></div>
      </div>
    </div>
  );
}

const UsagePage = ({ user }: UsagePageProps) => {
  const [fromx, setFromx] = useState(() => moment([moment().year(), moment().month(), moment().date()]));
  const [tox, setTox] = useState(() => moment([moment().year(), moment().month(), moment().date()]).add(1, 'days'));
  const [nodes, setNodes] = useState<ChannelNode[]>([]);

  const update = (newFrom: moment.Moment, newTo: moment.Moment) => {
    setFromx(newFrom);
    setTox(newTo);
  };

  return (
    <Layout user={user} navAlwaysOn>
      <SwipePane fromx={fromx} tox={tox} nodes={nodes}
            update={update} updatenodes={setNodes} />
    </Layout>
  );
};

export default UsagePage;
